import { type FC, useCallback, useEffect, useState } from "react";
import {
  type ClientImage,
  type Props,
} from "@clientFrontend/features/publication/components/ClientImageLibraryModal/type";
import { Presentation } from "./Presentation";

export const ClientImageLibraryModal: FC<Props> = ({
  mode,
  maxCount,
  initialSelectedImages,
  clientImages,
  clientImageUrl,
  headerTitle,
  renderFooter,
  fetchClientImages,
  setClientImages,
  onConfirm,
  onClose,
}) => {
  // initialSelectedImages: 選択された画像(初期値)
  // selectedImages: 選択された画像(初期値) + モーダル内で選択された画像
  const [selectedImages, setSelectedImages] = useState<ClientImage[]>(initialSelectedImages);

  const handleClickClientImageCassette = useCallback(
    (clientImage: ClientImage) => {
      const isSelected = selectedImages.some((image) => image.id === clientImage.id);
      if (isSelected) {
        setSelectedImages(selectedImages.filter((image) => image.id !== clientImage.id));
        return;
      }
      // 単一選択は最大1枚。既に選択済みの状態で別画像を選ぶとアラートで弾く(変更するには一度選択を解除する)
      if (mode === "single" && selectedImages.length >= 1) {
        alert("画像は1枚までしか選択できません");
        return;
      }
      setSelectedImages([...selectedImages, clientImage]);
    },
    [selectedImages, mode],
  );

  const handleClickBulkSelectButton = useCallback(() => {
    if (maxCount !== undefined && selectedImages.length > maxCount) {
      alert(`画像は${maxCount}枚までしか設定できません`);
      return;
    }
    const hasGif = selectedImages.some((image) => image.filename.toLowerCase().includes(".gif"));
    // 画像ライブラリに格納されているgif画像を選択して「画像を設定する」した場合にエラーを表示
    // gif画像は新規で作成されないが、既存では存在するためここで設定できないようにする
    if (hasGif) {
      alert("設定する画像にgif画像が含まれています");
      return;
    }
    // 選択画像を呼び出し側へ返す(フォーム反映は呼び出し側の責務)
    onConfirm(selectedImages);
    onClose();
  }, [selectedImages, maxCount, onConfirm, onClose]);

  const handleClearSelectedImages = useCallback(() => {
    setSelectedImages([]);
  }, []);

  // ClientImageを一括でAPIから取得する
  // ※モーダルを最初に開いた時の1回のみしか実行させたくない
  useEffect(() => {
    fetchClientImages();
  }, [fetchClientImages]);

  return (
    <Presentation
      clientImages={clientImages}
      selectedImageIds={selectedImages.map((image) => image.id)}
      clientImageUrl={clientImageUrl}
      headerTitle={headerTitle}
      footerContent={renderFooter({
        selectedCount: selectedImages.length,
        onConfirm: handleClickBulkSelectButton,
      })}
      setClientImages={setClientImages}
      handleClickClientImageCassette={handleClickClientImageCassette}
      handleClearSelectedImages={handleClearSelectedImages}
      handleCloseModal={onClose}
    />
  );
};
